import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faEdit, 
  faTrashAlt, 
  faEye, 
  faPlus,
  faToggleOn,
  faToggleOff,
  faMagic
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';

function MembershipPlansList() {
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);
  
  useEffect(() => {
    fetchMembershipPlans();
  }, [currentPage, filter]);
  
  const fetchMembershipPlans = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        status: filter.status,
        search: searchTerm
      };
      
      const response = await adminService.getMembershipPlans(params);
      
      setMembershipPlans(response.plans);
      setTotalPages(response.pages);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching membership plans:', error);
      setIsLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchMembershipPlans();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page on filter change
  };
  
  const handleDelete = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    try {
      await adminService.deleteMembershipPlan(planToDelete.id);
      
      setMembershipPlans(membershipPlans.filter(plan => plan.id !== planToDelete.id));
      setShowDeleteModal(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error('Error deleting membership plan:', error);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPlanToDelete(null);
  };

  const togglePlanStatus = async (plan) => {
    try {
      const response = await adminService.toggleMembershipPlanStatus(plan.id);
      
      const updatedPlans = membershipPlans.map(p => {
        if (p.id === plan.id) {
          return {
            ...p,
            status: response.status
          };
        }
        return p;
      });
      
      setMembershipPlans(updatedPlans);
    } catch (error) {
      console.error('Error toggling plan status:', error);
    }
  };
  
  const handleCreateDefaultPlans = async () => {
    if (window.confirm('This will create 6 default membership plans. Continue?')) {
      setIsCreatingDefaults(true);
      try {
        const response = await adminService.createDefaultMembershipPlans();
        
        // Refresh plans list after creation
        fetchMembershipPlans();
        alert('Default membership plans created successfully!');
      } catch (error) {
        console.error('Error creating default membership plans:', error);
        alert('Failed to create default plans. Please try again.');
      } finally {
        setIsCreatingDefaults(false);
      }
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Membership Plans</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">Membership Plans</li>
        </ul>
      </div>
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginRight: '1rem' }}>
                <input 
                  type="text" 
                  placeholder="Search plans..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid var(--admin-border)',
                    minWidth: '250px'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Search
              </button>
            </form>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <select
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              
              <button 
                className="admin-btn admin-btn-secondary" 
                onClick={handleCreateDefaultPlans}
                disabled={isCreatingDefaults}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d'
                }}
              >
                <FontAwesomeIcon icon={faMagic} />
                {isCreatingDefaults ? 'Creating...' : 'Create Default Plans'}
              </button>
              
              <Link to="/admin/membership-plans/add" className="admin-btn admin-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}>
                <FontAwesomeIcon icon={faPlus} />
                Add Plan
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-table-container">
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Plan Name</th>
                <th>Price</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {membershipPlans.length > 0 ? (
                membershipPlans.map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.id}</td>
                    <td>
                      <div style={{ fontWeight: '600' }}>{plan.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6c757d', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {plan.description}
                      </div>
                    </td>
                    <td style={{ fontWeight: '600' }}>{formatCurrency(plan.price)}</td>
                    <td>
                      <span 
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          backgroundColor: plan.status === 'active' ? '#d4edda' : '#f8d7da',
                          color: plan.status === 'active' ? '#155724' : '#721c24'
                        }}
                      >
                        {plan.status}
                      </span>
                    </td>
                    <td>{formatDate(plan.created_at)}</td>
                    <td>
                      <div className="admin-table-actions">
                        <Link 
                          to={`/admin/membership-plans/${plan.id}`} 
                          className="admin-table-btn admin-table-btn-view"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <Link 
                          to={`/admin/membership-plans/${plan.id}/edit`} 
                          className="admin-table-btn admin-table-btn-edit"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        <button 
                          className="admin-table-btn admin-table-btn-toggle"
                          onClick={() => togglePlanStatus(plan)}
                          title={plan.status === 'active' ? 'Deactivate' : 'Activate'}
                          style={{ 
                            backgroundColor: plan.status === 'active' ? '#fff3cd' : '#d4edda',
                            color: plan.status === 'active' ? '#856404' : '#155724'
                          }}
                        >
                          <FontAwesomeIcon icon={plan.status === 'active' ? faToggleOn : faToggleOff} />
                        </button>
                        <button 
                          className="admin-table-btn admin-table-btn-delete"
                          onClick={() => handleDelete(plan)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                    No membership plans found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            padding: '1rem', 
            borderTop: '1px solid var(--admin-border)'
          }}>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
              >
                &laquo;
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className="admin-btn"
                  style={{ 
                    backgroundColor: currentPage === i + 1 ? 'var(--admin-primary)' : '#f8f9fa',
                    color: currentPage === i + 1 ? 'white' : 'var(--admin-dark)',
                    padding: '0.5rem 0.75rem'
                  }}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa',
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
      
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Confirm Deletion</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Are you sure you want to delete the <strong>{planToDelete?.name}</strong> membership plan? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
                style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default MembershipPlansList;
